import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import React, { useMemo, useState, useEffect } from 'react';
import styles from './escalatedTable.module.css';
import { useEscalationInfo } from '../../Context/escalationContext';
import { IEscalatedPageDetailModel } from '../../Models/escalatedPageModels';
import { Table, Tooltip } from 'antd';
import { formatUTCDateTime } from '../../utils/formatUTCDate';
import CopyLinkIcon from '../Icons/copyLink';
import copyToClipboard from '../../utils/copyToClipboard';
import { toast } from 'react-toastify';
import { ESCALATION_TYPE_KEY, HIDDEN_COMBINED_NAME_KEYS, STATUS_COLORS } from '../../Pages/escalatedPage/escalatedPage.constants';
import { DefaultOptionColor } from '../../utils/constants/OptionsColors';
import { APPLICATION_ESCALATION_UID_KEY, ESCALATION_STATUS_KEY } from '../CreateEscalation/createEscalation.constants';
import { CONVERSATION_ID, OPPORTUNITY_UID } from '../../utils/constants/applicationDetailsBifurcation';
import { useInsightsUtils } from '../../hooks/useInsightsUtils';
import { ISortObj } from '../../Models/applicationModels';
import {
    getCombinedEscalationNameValue,
    getEscalationColumnLabel
} from '../../Pages/escalatedPage/escalatedPage.table.utils';

interface IEscalatedTableProps {
    headerNames: string[];
    visibleColumns: string[];
    handleCloseEscalationPopUp?: (flagUpdateData: boolean, oppId: string) => Promise<void> | void;
    onSortChange: (sortItem: ISortObj | null) => void;
}

const copyIconFields = new Set([OPPORTUNITY_UID, CONVERSATION_ID, APPLICATION_ESCALATION_UID_KEY]);

const COLUMN_WIDTH = {
    [ESCALATION_STATUS_KEY]: 100,
}

const dateTimeKeys = new Set([
    "createdOn",
    "eventDateTime",
    "paymentOn",
    "lastLnidCallOn",
    "threePsStartedOn",
    "threePsCompletedOn",
    "escalatedAt",
    "updatedAt"
]);

const sortableKeys = new Set([
    "escalatedAt",
    "updatedAt",
]);

/** Space taken by layout header, page header, pagination, padding (px). Table height = viewport - this. */
const VIEWPORT_OFFSET = 320;
const MIN_TABLE_BODY_HEIGHT = 300;
/** Approximate row height for Ant Design Table size="middle" (px). */
const ROW_HEIGHT_ESTIMATE = 48;

const getTableBodyHeight = () =>
    Math.max(MIN_TABLE_BODY_HEIGHT, typeof window !== 'undefined' ? window.innerHeight - VIEWPORT_OFFSET : MIN_TABLE_BODY_HEIGHT);

const EscalatedTable = (props: IEscalatedTableProps) => {
    const { escalatedData, escalationList, sortInfo } = useEscalationInfo();
    const { HandleAddEscalation } = useInsightsUtils();
    const { visibleColumns, handleCloseEscalationPopUp, onSortChange } = props;
    const [scrollBodyHeight, setScrollBodyHeight] = useState(getTableBodyHeight);

    useEffect(() => {
        const onResize = () => setScrollBodyHeight(getTableBodyHeight());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const rowCount = escalatedData?.length ?? 0;
    const estimatedContentHeight = rowCount * ROW_HEIGHT_ESTIMATE;
    const needsVerticalScroll = estimatedContentHeight > scrollBodyHeight;
    const scrollConfig = useMemo(
        () => ({
            x: 'max-content' as const,
            ...(needsVerticalScroll && { y: scrollBodyHeight }),
        }),
        [needsVerticalScroll, scrollBodyHeight]
    );

    const columns: ColumnsType<IEscalatedPageDetailModel> = useMemo(() => {
        const data = escalatedData ?? [];
        const orderedKeys = data.length > 0 ? Object.keys(data[0]) as (keyof IEscalatedPageDetailModel)[] : [];

        let visibleIndex = 0;

        const dataColumns = orderedKeys
            .filter((keyName) => {
                if (HIDDEN_COMBINED_NAME_KEYS.has(keyName)) {
                    return false;
                }
                return visibleColumns.includes(getEscalationColumnLabel(keyName));
            })
            .map((keyName) => {
                const isSortable = sortableKeys.has(keyName);
                const isActiveSort = isSortable && sortInfo?.value === String(keyName);
                const isFixedColumn = visibleIndex < 4;
                const width = COLUMN_WIDTH[keyName as keyof typeof COLUMN_WIDTH];
                const col: { title: React.ReactNode; dataIndex: keyof IEscalatedPageDetailModel; key: string; width?: number; fixed?: 'left'; render?: (value: unknown, row: IEscalatedPageDetailModel) => React.ReactNode; sorter?: boolean; sortOrder?: 'ascend' | 'descend' | null; } = {
                    title: getEscalationColumnLabel(keyName),
                    dataIndex: keyName,
                    key: keyName,
                    width,
                };
                if (isFixedColumn) {
                    col.fixed = 'left';
                }
                col.sorter = isSortable ? true : undefined;
                col.sortOrder = isActiveSort
                    ? (sortInfo?.sortDescending ? 'descend' : 'ascend')
                    : null;
                col.render = (value: unknown, row: IEscalatedPageDetailModel) => {
                    const cellValue = getCombinedEscalationNameValue(keyName, row);
                    const colorForStatus = STATUS_COLORS[cellValue.toLowerCase() as keyof typeof STATUS_COLORS] || DefaultOptionColor
                    if (dateTimeKeys.has(keyName)) {
                        return cellValue?.length > 0 ? formatUTCDateTime(cellValue, false, true) : "-";
                    }
                    if (keyName === ESCALATION_STATUS_KEY) {
                        return <span style={{ color: colorForStatus }}>{cellValue}</span>;
                    }
                    if (keyName === ESCALATION_TYPE_KEY) {
                        const escalationName = escalationList?.find((e) => e.escalationUid === cellValue)?.escalationName;
                        return <span onClick={() => HandleAddEscalation({
                                    oppId: row.opportunityUid,
                                    escalationId: cellValue,
                                    escalationName: escalationName ?? "",
                                    agencyName: row.tenantTitle,
                                    applicationEscalationUid: row.applicationEscalationUid,
                                    handleCloseEscalationPopUp
                                })} className={styles.escalationName}>{escalationName ?? "-"}</span>;
                    }
                    if (copyIconFields.has(keyName)) {
                        const handleCopy = () => copyToClipboard(cellValue, () => toast.success('Copied to clipboard'));
                        return <span>{cellValue} <CopyLinkIcon width={12} height={13} className={styles.copyLinkIcon} onClick={handleCopy} /></span>;
                    }
                    return (
                        <Tooltip title={cellValue?.length > 50 ? cellValue : ""}>
                            {(cellValue?.length > 50 && !copyIconFields.has(keyName)) ? cellValue.slice(0, 25) + "..." : cellValue}
                        </Tooltip>
                    );
                };
                visibleIndex += 1;
                return col;
            });

        return dataColumns;
    }, [escalatedData, visibleColumns, escalationList, HandleAddEscalation, handleCloseEscalationPopUp]);

    const handleTableSortChange = (sorter: SorterResult<IEscalatedPageDetailModel> | SorterResult<IEscalatedPageDetailModel>[]) => {
        const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        const sortOrder = activeSorter?.order;
        const sortField = activeSorter?.field ?? activeSorter?.columnKey;

        if (!sortOrder || !sortField) {
            onSortChange(null);
            return;
        }

        onSortChange({
            value: String(sortField),
            sortDescending: sortOrder === 'descend',
        });
    };

    return (
        <div className={styles.escalatedTableContainer}>
            <div className={styles.tableWrapper}>
                <Table<IEscalatedPageDetailModel>
                    columns={columns}
                    bordered
                    dataSource={escalatedData ?? []}
                    rowKey={(row) => row.applicationEscalationUid}
                    scroll={scrollConfig}
                    pagination={false}
                    rowClassName={(_, index) =>
                        index % 2 === 0 ? styles.evenRowStyle : styles.oddRowStyle
                    }
                    className={styles.escalatedTable}
                    size="middle"
                    onChange={(_, __, sorter) => handleTableSortChange(sorter)}
                    components={{
                        header: {
                            cell: ({ className, ...restProps }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
                                <th {...restProps} className={`${className} ${styles.escalatedTableHead}`} />
                            ),
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default EscalatedTable;